import { cn } from '@/lib/cn';
import { type JSX, Show, createEffect, createSignal, createUniqueId, onCleanup } from 'solid-js';
import { Portal } from 'solid-js/web';
import { Motion, Presence } from 'solid-motionone';

/** What UserInfo needs from a user — matches the shared UserRef payload snippet. */
export interface UserInfoUser {
  displayName: string;
  email?: string | null;
  avatarUrl?: string | null;
}

const initials = (name: string, email = ''): string => {
  const base = name || email;
  if (!base) return '?';
  const parts = base.split(/[\s@]+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${(parts[0] as string)[0] ?? ''}${(parts[1] as string)[0] ?? ''}`.toUpperCase();
  }
  return base.slice(0, 2).toUpperCase();
};

export const Avatar = (props: { user: UserInfoUser; class?: string }): JSX.Element => {
  // Rows remount on refetch, so a one-way broken flag is enough for img fallback.
  const [broken, setBroken] = createSignal(false);
  return (
    <Show
      when={props.user.avatarUrl && !broken()}
      fallback={
        <div
          class={cn(
            'flex shrink-0 items-center justify-center rounded-full bg-brand-600 font-medium text-white',
            props.class,
          )}
        >
          {initials(props.user.displayName, props.user.email ?? '')}
        </div>
      }
    >
      <img
        src={props.user.avatarUrl as string}
        alt=""
        class={cn('shrink-0 rounded-full object-cover', props.class)}
        onError={() => setBroken(true)}
      />
    </Show>
  );
};

const OPEN_DELAY_MS = 150;
/** Grace period to travel from the trigger into the card (so email is copyable). */
const CLOSE_DELAY_MS = 100;
/** Rough card height used only to decide whether to flip above the trigger. */
const CARD_ESTIMATE_PX = 96;
const CARD_MAX_W_PX = 288;

export interface UserInfoProps {
  user: UserInfoUser | null | undefined;
  class?: string;
}

/**
 * Avatar + display name (email fallback); hover or keyboard focus reveals a
 * hover card with the larger avatar, name, and email (UCH-444).
 */
export const UserInfo = (props: UserInfoProps): JSX.Element => {
  const [open, setOpen] = createSignal(false);
  const [anchor, setAnchor] = createSignal<DOMRect | null>(null);
  const cardId = createUniqueId();
  let triggerRef: HTMLSpanElement | undefined;
  let openTimer: ReturnType<typeof setTimeout> | undefined;
  let closeTimer: ReturnType<typeof setTimeout> | undefined;

  const show = () => {
    clearTimeout(closeTimer);
    setAnchor(triggerRef?.getBoundingClientRect() ?? null);
    setOpen(true);
  };
  const scheduleShow = () => {
    clearTimeout(openTimer);
    openTimer = setTimeout(show, OPEN_DELAY_MS);
  };
  const scheduleHide = () => {
    clearTimeout(openTimer);
    clearTimeout(closeTimer);
    closeTimer = setTimeout(() => setOpen(false), CLOSE_DELAY_MS);
  };
  onCleanup(() => {
    clearTimeout(openTimer);
    clearTimeout(closeTimer);
  });

  createEffect(() => {
    if (!open()) return;
    const update = () => setAnchor(triggerRef?.getBoundingClientRect() ?? null);
    const onKeydown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    document.addEventListener('keydown', onKeydown);
    onCleanup(() => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
      document.removeEventListener('keydown', onKeydown);
    });
  });

  // Portal + fixed positioning so the card escapes overflow containers (table
  // wrappers); flips above the trigger when it would overflow the viewport.
  const cardStyle = (): JSX.CSSProperties => {
    const rect = anchor();
    if (!rect) return {};
    const below = rect.bottom + 6 + CARD_ESTIMATE_PX <= window.innerHeight;
    return {
      position: 'fixed',
      left: `${Math.max(8, Math.min(rect.left, window.innerWidth - CARD_MAX_W_PX - 8))}px`,
      ...(below
        ? { top: `${rect.bottom + 6}px` }
        : { bottom: `${window.innerHeight - rect.top + 6}px` }),
      'z-index': 60,
    };
  };

  const duration = () => (window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 0.2);

  return (
    <Show when={props.user} fallback={<span class={props.class}>—</span>}>
      {(u) => (
        <>
          <span
            ref={triggerRef}
            tabindex="0"
            aria-describedby={open() ? cardId : undefined}
            class={cn(
              'inline-flex max-w-full cursor-default items-center gap-2 rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-brand-600',
              props.class,
            )}
            onMouseEnter={scheduleShow}
            onMouseLeave={scheduleHide}
            onFocusIn={show}
            onFocusOut={scheduleHide}
          >
            <Avatar user={u()} class="h-6 w-6 text-[10px]" />
            <span class="truncate">{u().displayName || u().email}</span>
          </span>
          <Portal>
            <Presence exitBeforeEnter>
              <Show when={open()}>
                <Motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: duration() }}
                  id={cardId}
                  role="tooltip"
                  style={cardStyle()}
                  onMouseEnter={show}
                  onMouseLeave={scheduleHide}
                  class="flex w-max max-w-72 items-center gap-3 rounded-xl border border-neutral-200 bg-surface p-3 shadow-lg dark:border-neutral-700 dark:bg-surface-dark"
                >
                  <Avatar user={u()} class="h-12 w-12 text-base" />
                  <div class="min-w-0">
                    <div class="truncate text-sm font-medium">{u().displayName || u().email}</div>
                    <Show when={u().email}>
                      <div class="truncate text-xs text-neutral-500">{u().email}</div>
                    </Show>
                  </div>
                </Motion.div>
              </Show>
            </Presence>
          </Portal>
        </>
      )}
    </Show>
  );
};
